import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { withRouter } from "react-router-dom";
import { select, selectAll } from 'd3-selection';
import { timeDay, timeMonth, timeYear } from 'd3-time';

import ConditionalLink from './ConditionalLink';
import Day from './Day';
import DayFilterContext from '../DayFilterContext';
import DayPicker from './DayPicker';
import PresentationContext from '../PresentationContext';
import { getView } from '../Routes';
import { dateNames, parseDate, urlDateFormat } from '../utils/formats';
import { usePrevious } from '../utils/react';
import './Dashboard.css';
import ErrorBoundary from '../ErrorBoundary';

function getDayScrollKeyHandler({ activeDayNames, activeDays, day, days, setDay }) {
  return function handleKeyPressForSingleDayScroll(event) {
    const { synthEvent } = event || {};
    const myEvent = synthEvent || event;
    let i;
    switch (myEvent.key) {
      case 'ArrowLeft':
        i = -1;
        break;
      case 'ArrowRight':
        i = 1;
        break;
      default:
        return;
    }
    if (!synthEvent) {
      event.preventDefault();
    }
    if ((myEvent.controlKey || myEvent.metaKey) && myEvent.shiftKey) {
      const d1 = parseDate(day);
      const d2 = timeYear.offset(d1, i);
      i = timeDay.count(d1, d2);
    } else if (myEvent.controlKey || myEvent.metaKey) {
      const d1 = parseDate(day);
      const d2 = timeMonth.offset(d1, i);
      i = timeDay.count(d1, d2);
    } else if (myEvent.shiftKey) {
      i *= 7;
    }
    if (i) {
      const currentIndex = days.indexOf(day);
      let newIndex = currentIndex + i;
      while ((newIndex >= 0) && (newIndex < (days.length - 1))) {
        // Verify that the new date is active.
        if (activeDays[days[newIndex]]) {
          setDay(days[newIndex]);
          return;
        }
        // If it is not, roll the date one by one backward or forward,
        // based on which direction we're navigating
        i += i < 0 ? -1 : 1;
        newIndex = currentIndex + i;
      }
      // The new date is outside the previously selected range
      // Just push the new date, and expect the context to select dates for us.
      // Still, make sure that the week day is not filtered out.
      const date = timeDay.offset(parseDate(day), i);
      const dayNumber = date.getDay();
      for (let j = 0; j < 7; j += 1) {
        const roll = (i < 0 ? -1 : 1) * j;
        const dayName = dateNames[(7 + dayNumber + roll) % 7];
        if (activeDayNames[dayName]) {
          setDay(urlDateFormat(timeDay.offset(date, roll)));
          return;
        }
      }
    }
  };
}

export function getDaysScrollKeyHandler({ days, setDays, weekInterval }) {
  const firstDay = parseDate(days[0]);
  const lastDay = parseDate(days[days.length - 1]);
  const dateCount = days.length;

  let d1;
  let d2;
  return function handleKeyPressForDaysScroll(event) {
    const { synthEvent } = event || {};
    const myEvent = synthEvent || event;
    let change;
    switch (myEvent.key) {
      case 'ArrowLeft':
        change = -1;
        break;
      case 'ArrowRight':
        change = 1;
        break;
      default:
        return;
    }
    if (!synthEvent) {
      event.preventDefault();
    }
    let roll;
    if ((myEvent.controlKey || myEvent.metaKey) && myEvent.shiftKey) {
      roll = timeYear;
    } else if (myEvent.controlKey || myEvent.metaKey) {
      roll = timeMonth;
    } else if (myEvent.shiftKey) {
      roll = weekInterval;
    } else {
      roll = timeDay;
    }
    if (roll === timeDay) {
      if (dateCount === 14) {
        d1 = weekInterval(weekInterval.offset(firstDay, change * 2));
        d2 = timeDay.offset(d1, dateCount - 1);
      } else if (change < 1) {
        d2 = roll.offset(firstDay, change);
        d1 = timeDay.offset(d2, -dateCount + 1);
      } else {
        d1 = roll.offset(lastDay, change);
        d2 = timeDay.offset(d1, dateCount - 1);
      }
    } else {
      d1 = roll.offset(firstDay, change);
      d2 = timeDay.offset(d1, dateCount - 1);
    }
    setDays(d1, d2);
  };
}

function getAllDays() {
  return dateNames.reduce((o, n) => {
    o[n] = true;
    return o;
  }, {});
}

function getAllDaysAsActive(days) {
  return days.reduce((o, d) => {
    o[d] = true;
    return o;
  }, {});
}

function isWeekDayActive(days, activeDays, dayName) {
  const weekDayInstances = days.filter(d => dateNames[parseDate(d).getDay()] === dayName);
  return weekDayInstances.some(d => activeDays[d]);
}

function Days(props) {
  const [activeDayNames, setActiveDayNames] = useState(getAllDays());
  const { activeDays, day, days: propsDays, invalidRange, setActiveDays, setDay, setDays: setContextDays, toggleDay } = useContext(DayFilterContext);

  const { formatDate, dateNumberFormat, week, weekDays, weekInterval, yearAndWeekNumberFormat } = useContext(PresentationContext);
  const { data, glucosePath, history, interactive, timeScale, t } = props;
  const path = history.location.pathname;
  const view = useMemo(() => getView(path), [path]);
  
  const days = useMemo(() => {
    let days = propsDays;
    if (!propsDays.length) {
      const { location } = history;
      const { pathname = '' } = location;
      const daysMatch = pathname.match(/(\d{4}-\d{2}-\d{2})/g) || [];
      if (daysMatch && (daysMatch.length > 1) && (daysMatch[0] > daysMatch[1])) {
        // Not a proper range!
        days = [daysMatch[0], daysMatch[daysMatch.length - 1]];
      }
    }
    return days;
  }, [history, propsDays]);

  const setDays = useMemo(() => function setDay(from, to) {
    setContextDays(timeDay.range(from, timeDay.offset(to, 1)).map(urlDateFormat));
  }, [setContextDays]);

  const inputHandler = useMemo(() => view === 'day'
  ? getDayScrollKeyHandler({ activeDayNames, activeDays, day, days, setDay })
  : getDaysScrollKeyHandler({ days, setDays, weekInterval }),
  [activeDayNames, activeDays, day, days, setDay, setDays, view, weekInterval]);
  
  useEffect(() => {
    if (interactive) {
      select(document).on('keydown.scrollDays', inputHandler);
    }
    return () => {
      select(document).on('keydown.scrollDays', null);
    };
  }, [inputHandler, interactive]);

  const weeks = useMemo(() => days.reduce((o, d) => {
    const day = parseDate(d);
    const weekNumber = yearAndWeekNumberFormat(day);
    // TODO: check whether the week number is the last of the year and should be joined with the next one?
    const dayNumber = dateNumberFormat(day);
    if (!o[weekNumber]) {
      o[weekNumber] = {};
    }
    o[weekNumber][dateNames[dayNumber]] = d;
    return o;
  }, {}), [days, dateNumberFormat, yearAndWeekNumberFormat]);

  const previousDays = usePrevious(days);

  useEffect(() => {
    if (previousDays && previousDays !== days) {
      // We need to set inactive days again, explicitly
      const newActiveDays = days.reduce((o, d) => {
        o[d] = activeDayNames[dateNames[parseDate(d).getDay()]];
        return o;
      }, {});
      setActiveDays(newActiveDays);
    }
  }, [activeDayNames, days, previousDays, setActiveDays]);

  const weekNumbers = Object.keys(weeks).sort();
  const weeksArray = weekNumbers.map((n) => weeks[n]);

  function toggleWeekDay(name) {
    let newActiveDayNames = { ...activeDayNames, [name]: !activeDayNames[name] };
    let newActiveDays;
    if (!Object.values(newActiveDayNames).some(v => v)) {
      // All deselected, restore all to selected
      newActiveDayNames = getAllDays();
      newActiveDays = getAllDaysAsActive(days);
    } else {
      newActiveDays = { ...activeDays };
      // Select all days of this week day
      const weekDayInstances = days.filter(d => dateNames[parseDate(d).getDay()] === name);
      weekDayInstances.forEach((d) => {
        newActiveDays[d] = newActiveDayNames[name];
      });
    }
    setActiveDays(newActiveDays);
    setActiveDayNames(newActiveDayNames);
  }

  function selectWeekDay(name) {
    // Select the current day, deselect all others
    setActiveDayNames(dateNames.reduce((o, n) => {
      o[n] = n === name ? true : false;
      return o;
    }, {}));
    const newActiveDays = days.reduce((o, d) => {
      o[d] = dateNames[parseDate(d).getDay()] === name;
      return o;
    }, {});
    setActiveDays(newActiveDays);
  }

  function checkAndToggleDay(day) {
    const dayName = dateNames[parseDate(day).getDay()];
    if (activeDayNames[dayName]) {
      // Day was active. If the last active day was inactivated, inactivate the day name.
      const weekDayInstances = days.filter(d => dateNames[parseDate(d).getDay()] === dayName);
      if (!weekDayInstances.some(d => activeDays[d] && (d !== day))) {
        toggleWeekDay(dayName);
      }
    } else {
      toggleWeekDay(dayName);
    }
    toggleDay(day);
  }

  const dayNavi = useMemo(() => (
    <ErrorBoundary>
      <button
        className="inline minimal"
        onClick={(event) => {
          inputHandler({ synthEvent: {
            ...event,
            key: 'ArrowLeft',
          } });
        }}
      >
        ‹
      </button>
      <DayPicker
        formatDate={formatDate}
        day={days[0]}
        error={invalidRange}
        setDate={(date) => {
          const validatedDate = new Date(date);
          if (!isNaN(validatedDate)) {
            const startDate = parseDate(date);
            setDays(startDate, timeDay.offset(startDate, days.length - 1));
          }
        }}
      />
      {t('common.rangeSeparator')}
      <DayPicker
        formatDate={formatDate}
        day={days[days.length - 1]}
        error={invalidRange}
        setDate={(date) => {
          const validatedDate = new Date(date);
          if (!isNaN(validatedDate)) {
            const startDate = parseDate(days[0]);
            const endDate = parseDate(date);
            setDays(startDate, endDate);
          }
        }}
      />
      <button
        className="inline minimal"
        onClick={(event) => {
          inputHandler({ synthEvent: {
            ...event,
            key: 'ArrowRight',
          } });
        }}
      >
        ›
      </button>
    </ErrorBoundary>
  ), [formatDate, days, inputHandler, invalidRange, setDays, t]);

  return (
    <nav id="days">
      <h2>{t('days.heading')} {dayNavi}</h2>
      <ErrorBoundary>
        <table>
          <colgroup>
            <col span="5" className="weekdays" />
            <col span="2" className="weekend" />
          </colgroup>
          <thead>
            <tr>
              {
                week.map((dayNumber, i) => {
                  const dayName = t(`common.days.${dayNumber}`);
                  const internalDayName = weekDays[i];
                  const activating = !isWeekDayActive(days, activeDays, internalDayName);
                  return (
                    <th
                      key={internalDayName}
                      title={t('days.filterToggleTitle', {day: dayNumber})}
                    >
                      <button
                        className={`interactive ${
                          isWeekDayActive(days, activeDays, internalDayName)
                          ? 'active'
                          : 'inactive'
                        }`}
                        tabIndex="0"
                        onClick={() => {
                          toggleWeekDay(internalDayName);
                          selectAll(`#days tbody td:nth-child(${i + 1}) svg`)
                            .classed('inactivating', false)
                            .classed('activating', false);
                        }}
                        onDoubleClick={(e) => {
                          selectWeekDay(internalDayName);
                          e.preventDefault();
                        }}
                        onMouseOver={() => {
                          selectAll(`#days tbody td:nth-child(${i + 1}) svg`)
                            .classed('inactivating', !activating)
                            .classed('activating', activating);
                        }}
                        onMouseOut={() => {
                          selectAll(`#days tbody td:nth-child(${i + 1}) svg`)
                            .classed('inactivating', false)
                            .classed('activating', false)
                            .classed('resetting', true)
                            .transition()
                            .on('end', function removeClass() {
                              select(this).classed('resetting', false);
                            });
                        }}
                      >
                        {dayName}
                      </button>
                    </th>
                  );
                })
              }
            </tr>
          </thead>
          <tbody>
            {
              weeksArray.map((w, i) => (
                <Fragment key={weekNumbers[i]}>
                  <tr id={weekNumbers[i]}>
                    {
                      weekDays.map((dayName) => {
                        const wDay = w[dayName];
                        return (
                          <td key={`week ${i} ${dayName}`}>
                            <ConditionalLink
                              active={interactive && wDay && (navigator.userAgent !== 'ReactSnap')}
                              onClick={(event) => {
                                event.preventDefault();
                                if (!wDay) {
                                  return;
                                }
                                setDay(wDay);
                              }}
                              to={`../day/${wDay}`}
                            >
                              <Day
                                data={data}
                                day={wDay}
                                dayName={dayName}
                                glucosePath={wDay ? glucosePath : undefined}
                                active={activeDays[wDay]}
                                timeScale={timeScale}
                              />
                            </ConditionalLink>
                          </td>
                        );
                      })
                    }
                  </tr>
                  <tr className="filter">
                    {
                      weekDays.map((dayName) => {
                        const wDay = w[dayName];
                        const wDayDate = new Date(wDay);
                        return (
                          <td key={`week ${i} ${dayName}`}>
                            {wDay
                              ? (
                                <ConditionalLink
                                  active={interactive && wDay}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    checkAndToggleDay(wDay)
                                  }}
                                  to={`../day/${wDay}`}
                                >
                                  <time dateTime={urlDateFormat(wDayDate)}>
                                    {formatDate(wDayDate)}
                                  </time>
                                </ConditionalLink>
                              )
                              : undefined
                            }
                          </td>
                        );
                      })
                    }
                  </tr>
                </Fragment>
              ))
            }
          </tbody>
        </table>
      </ErrorBoundary>
      <p><small>{t('days.description')}</small></p>
    </nav>
  );
}

export default withRouter(Days);
